import { Component, OnDestroy, computed, effect, inject, signal, untracked } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { Unsubscribe } from '@angular/fire/firestore';
import { LucideIconComponent } from '../../../shared/ui/lucide-icon/lucide-icon.component';
import { HeadingComponent } from '../../../shared/ui/typography/heading/heading.component';
import { TextComponent } from '../../../shared/ui/typography/text/text.component';
import { ButtonComponent } from '../../../shared/ui/button/button.component';
import { BadgeComponent } from '../../../shared/ui/badge/badge.component';
import { AsideService, ViewDriverAsideData } from '../../../core/services/aside.service';
import { UserService } from '../../../core/services/user.service';
import { AuthService } from '../../../core/services/auth.service';
import { PermissionsService } from '../../../core/services/permissions.service';
import { PayrollService } from '../../../core/services/payroll.service';
import { ModalService } from '../../../core/services/modal.service';
import { FormatPhonePipe } from '../../../shared/pipes/format-phone.pipe';
import {
  getPayTypeLabel,
  getFlatRatePeriodLabel,
  FlatRatePeriod,
} from '../../../core/models/payroll.model';
import {
  CarrierHistoryEntry,
  buildCompanyEmploymentTimelines,
} from '../../../core/models/user.model';
import { EditDriverModalComponent } from '../../team/edit-driver-modal/edit-driver-modal.component';
import { EditPayStructureModalComponent } from '../edit-pay-structure-modal/edit-pay-structure-modal.component';
import { AssignEquipmentModalComponent } from '../assign-equipment-modal/assign-equipment-modal.component';
import { TerminateDriverModalComponent } from '../terminate-driver-modal/terminate-driver-modal.component';
import { ReactivateDriverModalComponent } from '../reactivate-driver-modal/reactivate-driver-modal.component';

@Component({
  selector: 'app-driver-aside',
  standalone: true,
  imports: [
    CommonModule,
    LucideIconComponent,
    HeadingComponent,
    TextComponent,
    ButtonComponent,
    BadgeComponent,
    FormatPhonePipe,
  ],
  templateUrl: './driver-aside.component.html',
})
export class DriverAsideComponent implements OnDestroy {
  private readonly asideService = inject(AsideService);
  private readonly userService = inject(UserService);
  private readonly authService = inject(AuthService);
  private readonly perms = inject(PermissionsService);
  private readonly payrollService = inject(PayrollService);
  private readonly modalService = inject(ModalService);
  private readonly router = inject(Router);

  readonly context = this.asideService.asideContext;
  readonly canManageEmployment = computed(() => this.perms.canManageDriverEmployment());
  readonly canManageDrivers = computed(() => this.perms.canManageDrivers());
  readonly canEditDriverPay = computed(() => this.perms.canEditDriverPay());

  private readonly historyEvents = signal<CarrierHistoryEntry[]>([]);
  private historyUnsub: Unsubscribe | null = null;
  private watchedDriverId: string | null = null;

  readonly driver = computed(() => {
    const ctx = this.context();
    if (ctx?.type !== 'view-driver') return null;
    const driverId = (ctx.data as ViewDriverAsideData).id;
    return this.userService.users().get(driverId) || null;
  });

  readonly legalName = computed(() => {
    const d = this.driver();
    return this.userService.getLegalName(d) || 'Unnamed Driver';
  });

  readonly preferredName = computed(() => {
    const d = this.driver();
    const pref = this.userService.getPreferredName(d);
    const legal = this.legalName();
    if (pref && pref.toLowerCase() !== legal.toLowerCase()) {
      return pref;
    }
    return null;
  });

  readonly timelines = computed(() => {
    const d = this.driver();
    const companyId = this.authService.companyId();
    if (!d || !companyId) return [];
    return buildCompanyEmploymentTimelines(this.historyEvents(), d, companyId);
  });

  readonly payStructure = computed(() => {
    const d = this.driver();
    if (!d) return null;
    return this.payrollService.getDriverPayStructure(d.id);
  });

  readonly cdlStatus = computed(() => {
    const expStr = this.driver()?.cdlInfo?.expirationDate;
    if (!expStr) return { variant: 'secondary' as const, label: 'Unverified' };
    const exp = new Date(expStr);
    const diffDays = (exp.getTime() - Date.now()) / (1000 * 3600 * 24);
    if (diffDays < 0) return { variant: 'danger' as const, label: 'Expired' };
    if (diffDays <= 30) return { variant: 'warning' as const, label: `${Math.round(diffDays)}d left` };
    return { variant: 'success' as const, label: 'Valid CDL' };
  });

  constructor() {
    effect(() => {
      const driverId = this.driver()?.id || null;
      if (driverId === this.watchedDriverId) return;
      untracked(() => {
        this.teardownHistory();
        this.watchedDriverId = driverId;
        if (!driverId) return;
        this.historyUnsub = this.userService.watchDriverCompanyHistory(driverId, (entries) => {
          this.historyEvents.set(entries);
        });
      });
    });
  }

  ngOnDestroy(): void {
    this.teardownHistory();
  }

  private teardownHistory(): void {
    if (this.historyUnsub) {
      this.historyUnsub();
      this.historyUnsub = null;
    }
    this.historyEvents.set([]);
  }

  getPayTypeLabel(type?: string | null, period?: FlatRatePeriod | string | null): string {
    return getPayTypeLabel(type, period as any);
  }

  getFlatRatePeriodLabel(period?: FlatRatePeriod | string | null): string {
    return getFlatRatePeriodLabel(period as any);
  }

  view360(driverId: string): void {
    this.router.navigate(['/drivers', driverId]);
  }

  editProfile(userId: string): void {
    this.modalService.open(EditDriverModalComponent, {
      title: 'Edit Driver Profile',
      icon: 'Pencil',
      data: { userId },
    });
  }

  editPay(driverId: string): void {
    this.modalService.open(EditPayStructureModalComponent, {
      title: `Pay Structure — ${this.legalName()}`,
      subtitle: 'Manage compensation formulas and rates',
      icon: 'DollarSign',
      data: { driverId },
    });
  }

  assignEquipment(driverId: string): void {
    this.modalService.open(AssignEquipmentModalComponent, {
      title: `Assign Equipment — ${this.legalName()}`,
      subtitle: 'Link power unit and trailer',
      icon: 'Truck',
      data: { driverId },
    });
  }

  terminateDriver(driverId: string): void {
    this.modalService.open(TerminateDriverModalComponent, {
      title: `Terminate Driver — ${this.legalName()}`,
      subtitle: 'Record termination date and preserve employment history',
      icon: 'UserX',
      data: { driverId },
    });
  }

  reactivateDriver(driverId: string): void {
    this.modalService.open(ReactivateDriverModalComponent, {
      title: `Reactivate Driver — ${this.legalName()}`,
      subtitle: 'Start a new employment timeline with your company',
      icon: 'RotateCcw',
      data: { driverId },
    });
  }
}
